import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  MoneyOff as DebtIcon,
  Warning as WarningIcon,
  Schedule as OverdueIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllDebts } from '../../services/debtService';
import { Debt, DebtStatus } from '../../utils/debtTypes';
import { formatCurrency } from '../../utils/numberUtils';

interface DebtSummary {
  totalDebts: number;
  totalAmount: number;
  overdueDebts: number;
  overdueAmount: number;
  dueSoonDebts: number;
  dueSoonAmount: number;
  highValueDebts: number;
  highValueAmount: number;
}

const QuickDebtSummary: React.FC = () => {
  const [summary, setSummary] = useState<DebtSummary>({
    totalDebts: 0,
    totalAmount: 0,
    overdueDebts: 0,
    overdueAmount: 0,
    dueSoonDebts: 0,
    dueSoonAmount: 0,
    highValueDebts: 0,
    highValueAmount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadDebtSummary = async () => {
    try {
      setLoading(true);
      setError(null);

      const allDebtsFromDB = await getAllDebts(undefined, 500);
      
      // فلترة الديون النشطة والمسددة جزئياً
      const activeDebts = allDebtsFromDB.filter(debt =>
        debt.status === DebtStatus.ACTIVE || debt.status === DebtStatus.PARTIALLY_PAID
      );

      const now = new Date();
      const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);

      let overdueDebts = 0;
      let overdueAmount = 0;
      let dueSoonDebts = 0;
      let dueSoonAmount = 0;
      let highValueDebts = 0;
      let highValueAmount = 0;

      activeDebts.forEach(debt => {
        const remainingAmount = debt.remainingAmount || 0;

        // الديون المتأخرة
        if (debt.dueDate && new Date(debt.dueDate) < now) {
          overdueDebts++;
          overdueAmount += remainingAmount;
        }

        // الديون القريبة من الاستحقاق
        if (debt.dueDate && new Date(debt.dueDate) > now && new Date(debt.dueDate) <= threeDaysFromNow) {
          dueSoonDebts++;
          dueSoonAmount += remainingAmount;
        }

        // الديون عالية القيمة
        if (remainingAmount >= 5000) {
          highValueDebts++;
          highValueAmount += remainingAmount;
        }
      });

      const totalAmount = activeDebts.reduce((sum, debt) => sum + (debt.remainingAmount || 0), 0);

      setSummary({
        totalDebts: activeDebts.length,
        totalAmount,
        overdueDebts,
        overdueAmount,
        dueSoonDebts,
        dueSoonAmount,
        highValueDebts,
        highValueAmount
      });

    } catch (err) {
      console.error('Error loading debt summary:', err);
      setError('فشل في تحميل ملخص الديون');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDebtSummary();
  }, []);

  const handleViewDebts = () => {
    navigate('/admin/debts');
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="outlined"
            onClick={loadDebtSummary}
            startIcon={<RefreshIcon />}
            fullWidth
          >
            إعادة المحاولة
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', position: 'relative' }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DebtIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight="bold">
              ملخص الديون
            </Typography>
          </Box>
          <Box>
            <Tooltip title="تحديث">
              <IconButton size="small" onClick={loadDebtSummary}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="عرض التفاصيل">
              <IconButton size="small" onClick={handleViewDebts}>
                <ViewIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* إجمالي الديون */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            {summary.totalDebts}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            إجمالي الديون النشطة
          </Typography>
          <Typography variant="h6" color="error" fontWeight="bold">
            {formatCurrency(summary.totalAmount)}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* تفاصيل الديون */}
        <Grid container spacing={2}>
          {/* الديون المتأخرة */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <OverdueIcon sx={{ mr: 1, color: 'error.main', fontSize: 20 }} />
                <Typography variant="body2">متأخرة</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Chip
                  label={summary.overdueDebts}
                  color="error"
                  size="small"
                  sx={{ minWidth: 40 }}
                />
                <Typography variant="caption" display="block" color="error">
                  {formatCurrency(summary.overdueAmount)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* الديون القريبة من الاستحقاق */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WarningIcon sx={{ mr: 1, color: 'warning.main', fontSize: 20 }} />
                <Typography variant="body2">قريبة الاستحقاق</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Chip
                  label={summary.dueSoonDebts}
                  color="warning"
                  size="small"
                  sx={{ minWidth: 40 }}
                />
                <Typography variant="caption" display="block" color="warning.main">
                  {formatCurrency(summary.dueSoonAmount)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* الديون عالية القيمة */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ mr: 1, color: 'info.main', fontSize: 20 }} />
                <Typography variant="body2">عالية القيمة</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Chip
                  label={summary.highValueDebts}
                  color="info"
                  size="small"
                  sx={{ minWidth: 40 }}
                />
                <Typography variant="caption" display="block" color="info.main">
                  {formatCurrency(summary.highValueAmount)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* زر عرض التفاصيل */}
        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleViewDebts}
            startIcon={<DebtIcon />}
            sx={{ borderRadius: 2 }}
          >
            إدارة الديون
          </Button>
        </Box>

        {/* مؤشر الحالة */}
        {summary.overdueDebts > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: 'error.main',
              animation: 'pulse 2s infinite'
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default QuickDebtSummary;
